import { useEffect, useMemo, useRef, useState } from "react";
// Types from react-grid-layout are not available; fall back to 'any' to avoid TS errors
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  ArrowsPointingOutIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useLanguage } from "~/hooks/useLanguage";
import { chartsStrings } from "~/translations/dashboard/charts";

// We import CSS via root links, not here. See root.tsx links()
const ResponsiveGridLayout = WidthProvider(Responsive);

export type DashboardGridItem = {
  id: string;
  element: React.ReactNode;
  /**
   * Optional human-readable title shown in the "hidden charts" picker when the
   * user hides this item while editing the layout. If absent, the item id is
   * used as a fallback.
   */
  title?: string;
  /**
   * If true, this widget is hidden by default for first-time users (i.e. when
   * no `:hidden` localStorage entry exists yet). Once the user explicitly
   * shows or hides anything, their choice is persisted and takes precedence.
   */
  defaultHidden?: boolean;
  // default layout for lg breakpoint (12 cols)
  lg: any;
  // optional default layouts for other breakpoints
  md?: any;
  sm?: any;
  xs?: any;
  xxs?: any;
  xl?: any;
};

type DashboardGridProps = {
  storageKey?: string;
  items: DashboardGridItem[];
  /**
   * When true, the edit/reorder view collapses every widget to a compact
   * header-only bar (showing the item's `title`) instead of the full chart.
   * This makes reordering fast since the user only sees a short list of
   * headers. Real sizes are preserved and restored on save. Requires each
   * item to have a `title`. Defaults to false (full-content edit overlay).
   */
  headerOnlyEdit?: boolean;
  /**
   * Like `headerOnlyEdit`, but only collapses to header-only bars on mobile
   * viewports (< sm breakpoint). On larger screens the full-content edit
   * overlay is used. Requires each item to have a `title`. Defaults to false.
   */
  headerOnlyEditOnMobile?: boolean;
};

const DEFAULT_STORAGE_KEY = "dashboardGridLayout:v1";
const HIDDEN_SUFFIX = ":hidden";

const ALL_LAYOUT_BPS = ["xl", "lg", "md", "sm", "xs", "xxs"] as const;

/** Cramped Markets Data defaults (and older taller ones) to the current card height. */
function needsMarketsDataHeightMigration(entry: any): boolean {
  return (
    entry?.i === "markets-data" &&
    (entry.h === 3 || entry.h === 5) &&
    (entry.minH === 3 || entry.minH === 4 || entry.minH == null)
  );
}

function migrateMarketsDataHeight(layouts: any): any {
  if (!layouts || typeof layouts !== "object") return layouts;
  let changed = false;
  const next: any = { ...layouts };
  for (const bp of ALL_LAYOUT_BPS) {
    if (!Array.isArray(next[bp])) continue;
    // The predicate lives outside the loop so the mapper closes over nothing
    // mutable (no-loop-func); `changed` is set from the loop body instead.
    if (next[bp].some(needsMarketsDataHeightMigration)) changed = true;
    next[bp] = next[bp].map((entry: any) =>
      needsMarketsDataHeightMigration(entry)
        ? { ...entry, h: 4, minH: 3 }
        : entry
    );
  }
  return changed ? next : layouts;
}

/** Old Insights card mobile defaults that left empty space under the carousel. */
function needsInsightsXsMigration(entry: any): boolean {
  return (
    entry?.i === "insights" &&
    entry.h === 10 &&
    (entry.minH === 5 || entry.minH == null)
  );
}

function needsInsightsXxsMigration(entry: any): boolean {
  return (
    entry?.i === "insights" &&
    entry.h === 6 &&
    (entry.minH === 5 || entry.minH == null)
  );
}

function migrateInsightsMobileHeight(layouts: any): any {
  if (!layouts || typeof layouts !== "object") return layouts;
  let changed = false;
  const next: any = { ...layouts };

  if (Array.isArray(next.xs) && next.xs.some(needsInsightsXsMigration)) {
    changed = true;
    next.xs = next.xs.map((entry: any) =>
      needsInsightsXsMigration(entry) ? { ...entry, h: 7 } : entry
    );
  }
  if (Array.isArray(next.xxs) && next.xxs.some(needsInsightsXxsMigration)) {
    changed = true;
    next.xxs = next.xxs.map((entry: any) =>
      needsInsightsXxsMigration(entry) ? { ...entry, h: 5, minH: 4 } : entry
    );
  }

  return changed ? next : layouts;
}

/** Old budget calendar defaults clipped the 6th week of 31-day months. */
const BUDGET_CALENDAR_IDS = new Set([
  "daily-calendar",
  "daily-income-calendar",
]);
const BUDGET_CALENDAR_HEIGHT_BUMP: Partial<
  Record<(typeof ALL_LAYOUT_BPS)[number], Record<number, number>>
> = {
  xl: { 12: 14 },
  lg: { 12: 14 },
  md: { 13: 15 },
};

function migrateBudgetCalendarHeight(layouts: any): any {
  if (!layouts || typeof layouts !== "object") return layouts;
  let changed = false;
  const next: any = { ...layouts };
  for (const bp of ALL_LAYOUT_BPS) {
    const bumpMap = BUDGET_CALENDAR_HEIGHT_BUMP[bp];
    if (!bumpMap || !Array.isArray(next[bp])) continue;
    const shouldBump = (entry: any) =>
      BUDGET_CALENDAR_IDS.has(entry?.i) && bumpMap[entry.h] != null;
    if (next[bp].some(shouldBump)) changed = true;
    next[bp] = next[bp].map((entry: any) =>
      shouldBump(entry) ? { ...entry, h: bumpMap[entry.h] } : entry
    );
  }
  return changed ? next : layouts;
}

function migrateDashboardLayouts(layouts: any): any {
  return migrateBudgetCalendarHeight(
    migrateInsightsMobileHeight(migrateMarketsDataHeight(layouts))
  );
}

const breakpoints = {
  xl: 1600,
  lg: 1200,
  md: 996,
  sm: 768,
  xs: 480,
  xxs: 0,
} as const;
const cols = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2, xl: 16 } as const;

// While editing the layout we collapse every widget to a uniform, compact
// height so the grid becomes a short list of header bars. This lets the user
// reorder charts quickly without scrolling past full chart bodies. The real
// (per-item) heights are restored on every layout change, so saved layouts
// always keep each chart's intended size.
const EDIT_COMPACT_H = 2;

function readSavedLayoutItemIds(storageKey: string): Set<string> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const ids = new Set<string>();
    for (const layout of Object.values(parsed)) {
      if (!Array.isArray(layout)) continue;
      for (const entry of layout) {
        if (
          entry &&
          typeof entry === "object" &&
          typeof (entry as any).i === "string"
        ) {
          ids.add((entry as any).i);
        }
      }
    }
    return ids;
  } catch {
    return null;
  }
}

export default function DashboardGrid({
  items,
  storageKey = DEFAULT_STORAGE_KEY,
  headerOnlyEdit = false,
  headerOnlyEditOnMobile = false,
}: DashboardGridProps) {
  const [mounted, setMounted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const preEditLayoutsRef = useRef<any | null>(null);
  const preEditHiddenRef = useRef<string[] | null>(null);
  const { lang } = useLanguage();
  const tr =
    chartsStrings[lang as keyof typeof chartsStrings] ?? chartsStrings.HEB;
  const dragLabel = lang === "HEB" ? "גרור להזזה" : "Drag to move";

  const hiddenStorageKey = `${storageKey}${HIDDEN_SUFFIX}`;

  useEffect(() => setMounted(true), []);

  // Track whether we're on a mobile viewport (< sm breakpoint) so that
  // `headerOnlyEditOnMobile` can collapse widgets to header bars only on
  // small screens while keeping the full-content edit overlay on desktop.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${breakpoints.sm - 1}px)`);
    const update = () => setIsMobileViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Header-only edit is active when explicitly requested for all breakpoints,
  // or when requested for mobile and we're currently on a mobile viewport.
  const effectiveHeaderOnly =
    headerOnlyEdit || (headerOnlyEditOnMobile && isMobileViewport);

  // Build default layouts from items
  const defaultLayouts: any = useMemo(() => {
    const toLayoutsByBp = (bp: keyof typeof cols) =>
      items.map((it) => it[bp] ?? it.lg).map((l) => ({ ...l }));

    return {
      lg: toLayoutsByBp("lg"),
      md: toLayoutsByBp("md"),
      sm: toLayoutsByBp("sm"),
      xs: toLayoutsByBp("xs"),
      xxs: toLayoutsByBp("xxs"),
      xl: toLayoutsByBp("xl"),
    };
  }, [items]);

  const [layouts, setLayouts] = useState<any>(() => {
    if (typeof window === "undefined") return defaultLayouts;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return defaultLayouts;
      const parsed = JSON.parse(raw) as any;
      const migrated = migrateDashboardLayouts(parsed);
      if (migrated !== parsed) {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(migrated));
        } catch {}
      }
      return migrated;
    } catch {
      return defaultLayouts;
    }
  });

  const [hiddenIds, setHiddenIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(hiddenStorageKey);
      if (!raw) return items.filter((i) => i.defaultHidden).map((i) => i.id);
      const parsed = JSON.parse(raw);
      const saved = Array.isArray(parsed) ? (parsed as string[]) : [];
      const savedLayoutIds = readSavedLayoutItemIds(storageKey);
      const newlyDefaultHidden = items
        .filter((i) => i.defaultHidden && !savedLayoutIds?.has(i.id))
        .map((i) => i.id);
      return Array.from(new Set([...saved, ...newlyDefaultHidden]));
    } catch {
      return [];
    }
  });

  // NOTE: We intentionally do NOT prune hiddenIds against the current items list.
  // Conditional widgets often disappear/reappear as async data loads (e.g. the
  // budget tab's expense, prediction, gauge widgets are gated on data flags).
  // Dropping a hidden id here while its item is temporarily missing would lose
  // the user's explicit "hidden" preference the next time the widget reappears.
  // Stale ids are harmless: visibleItems / hiddenItems both filter against the
  // current items list, so phantom ids don't render anywhere.

  // Seed hidden state from `defaultHidden` flags whenever new items appear
  // (e.g. conditional widgets that mount only after data loads), as long as
  // the user has no saved hidden state yet. Once any explicit user toggle
  // persists `:hidden` to localStorage, this effect becomes a no-op and the
  // user's choice takes precedence forever after.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (items.length === 0) return;
    let saved: string | null = null;
    try {
      saved = window.localStorage.getItem(hiddenStorageKey);
    } catch {}
    if (saved !== null) return;
    const defaults = items.filter((i) => i.defaultHidden).map((i) => i.id);
    if (defaults.length === 0) return;
    setHiddenIds((prev) => {
      const merged = new Set(prev);
      let changed = false;
      for (const id of defaults) {
        if (!merged.has(id)) {
          merged.add(id);
          changed = true;
        }
      }
      return changed ? Array.from(merged) : prev;
    });
  }, [items, hiddenStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (items.length === 0) return;
    let saved: string | null = null;
    try {
      saved = window.localStorage.getItem(hiddenStorageKey);
    } catch {}
    if (saved === null) return;

    const savedLayoutIds = readSavedLayoutItemIds(storageKey);
    const defaults = items
      .filter((i) => i.defaultHidden && !savedLayoutIds?.has(i.id))
      .map((i) => i.id);
    if (defaults.length === 0) return;

    let savedIds: string[] = [];
    try {
      const parsed = JSON.parse(saved);
      savedIds = Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {}
    const persistedHiddenIds = Array.from(new Set([...savedIds, ...defaults]));
    if (persistedHiddenIds.length !== savedIds.length) {
      try {
        window.localStorage.setItem(
          hiddenStorageKey,
          JSON.stringify(persistedHiddenIds)
        );
      } catch {}
    }

    setHiddenIds((prev) => {
      const merged = new Set(prev);
      let changed = false;
      for (const id of defaults) {
        if (!merged.has(id)) {
          merged.add(id);
          changed = true;
        }
      }
      return changed ? Array.from(merged) : prev;
    });
  }, [items, hiddenStorageKey, storageKey]);

  const visibleItems = useMemo(
    () => items.filter((i) => !hiddenIds.includes(i.id)),
    [items, hiddenIds]
  );
  const hiddenItems = useMemo(
    () => items.filter((i) => hiddenIds.includes(i.id)),
    [items, hiddenIds]
  );

  /**
   * Reconcile layouts with the current items list.
   *
   * When the items array changes (e.g. a conditional widget becomes available
   * after data loads), make sure every item has a layout entry per breakpoint
   * that uses the item's defaults instead of react-grid-layout's 1x1 fallback.
   *
   * IMPORTANT: We do NOT drop layout entries for ids missing from `items`,
   * because conditional widgets often appear/disappear as async data loads.
   * Dropping them here would lose the user's saved positions for those
   * widgets the next time they reappear. Stale ids are pruned only on save.
   */
  useEffect(() => {
    setLayouts((prev: any) => {
      const next: any = {};
      let changed = false;
      for (const bp of Object.keys(cols) as Array<keyof typeof cols>) {
        const prevArr: any[] = Array.isArray(prev?.[bp]) ? prev[bp] : [];
        const byId = new Map(prevArr.map((l: any) => [l.i, l]));
        const merged = [...prevArr];
        // Heal 1×1 entries left over from the previous bug where conditional
        // widgets that mounted after the dashboard grid would be assigned
        // RGL's default 1×1 fallback by `synchronizeLayoutWithChildren`.
        // Detect them and restore the item's recommended size while keeping
        // the (possibly user-moved) x/y the saved entry already had.
        for (let i = 0; i < merged.length; i++) {
          const entry = merged[i];
          const def =
            items.find((it) => it.id === entry.i)?.[bp] ??
            items.find((it) => it.id === entry.i)?.lg;
          if (
            def &&
            entry.w === 1 &&
            entry.h === 1 &&
            (def.w > 1 || def.h > 1)
          ) {
            merged[i] = {
              ...entry,
              w: def.w,
              h: def.h,
              minW: def.minW ?? entry.minW,
              minH: def.minH ?? entry.minH,
            };
            changed = true;
          }
        }
        for (const item of items) {
          if (!byId.has(item.id)) {
            const def = (item[bp] ?? item.lg) as any;
            merged.push({ ...def });
            changed = true;
          }
        }
        next[bp] = merged;
      }
      const migrated = migrateDashboardLayouts(next);
      if (migrated !== next) changed = true;
      return changed ? migrated : prev;
    });
  }, [items]);

  const persistHidden = (ids: string[]) => {
    try {
      window.localStorage.setItem(hiddenStorageKey, JSON.stringify(ids));
    } catch {}
  };

  const removeChart = (id: string) => {
    setHiddenIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      // Persist outside of edit mode immediately; in edit mode, persist on save.
      if (!isEditing) persistHidden(next);
      return next;
    });
  };

  /**
   * Restore (or insert) a layout entry for `id` at its per-breakpoint default
   * size, anchored to the TOP of the grid so the user immediately sees the
   * chart they just re-added. Existing charts are pushed down by the height
   * of the inserted row(s); RGL's vertical compaction handles the reflow.
   * Used when re-adding a previously-hidden chart so it appears at its
   * recommended size instead of react-grid-layout's 1x1 fallback.
   */
  const restoreLayoutForIds = (ids: string[]) => {
    if (ids.length === 0) return;
    setLayouts((prev: any) => {
      const next: any = { ...prev };
      for (const bp of Object.keys(cols) as Array<keyof typeof cols>) {
        const colCount = cols[bp];
        const arr: any[] = Array.isArray(next?.[bp]) ? [...next[bp]] : [];
        // Drop any existing entries for the ids we're restoring so we use
        // fresh defaults rather than stale 1x1 fallbacks left by RGL.
        const keep = arr.filter((l: any) => !ids.includes(l.i));
        // Pre-compute the top row(s) for the restored charts so we know how
        // much vertical space to push existing charts down by.
        const restored: any[] = [];
        let cursorX = 0;
        let cursorY = 0;
        let rowHeight = 0;
        for (const id of ids) {
          const item = items.find((i) => i.id === id);
          if (!item) continue;
          const def = (item[bp] ?? item.lg) as any;
          const w = Math.min(def?.w ?? 1, colCount);
          const h = def?.h ?? 1;
          // If two restored charts fit side-by-side, place them in the same row;
          // otherwise wrap and start a new row below.
          if (cursorX + w > colCount) {
            cursorY += rowHeight;
            cursorX = 0;
            rowHeight = 0;
          }
          restored.push({
            ...def,
            i: id,
            x: cursorX,
            y: cursorY,
            w,
            h,
          });
          cursorX += w;
          rowHeight = Math.max(rowHeight, h);
        }
        const totalShift = cursorY + rowHeight;
        const shifted = keep.map((l: any) => ({
          ...l,
          y: (l.y ?? 0) + totalShift,
        }));
        next[bp] = [...restored, ...shifted];
      }
      return next;
    });
  };

  const addChart = (id: string) => {
    setHiddenIds((prev) => {
      if (!prev.includes(id)) return prev;
      const next = prev.filter((x) => x !== id);
      restoreLayoutForIds([id]);
      if (!isEditing) persistHidden(next);
      return next;
    });
  };

  const addAllHiddenCharts = () => {
    setHiddenIds((prev) => {
      if (prev.length === 0) return prev;
      restoreLayoutForIds(prev);
      const next: string[] = [];
      if (!isEditing) persistHidden(next);
      return next;
    });
  };

  /**
   * react-grid-layout's `synchronizeLayoutWithChildren` has two failure
   * modes that corrupt the persisted layout if we trust its emitted value
   * blindly:
   *
   *   1. It drops layout entries whose `i` doesn't match a currently
   *      mounted child. Conditional widgets (predictions, gauges,
   *      sunburst, calendar) are gated on async data (`clientTransactions`)
   *      that loads AFTER the dashboard mounts on every refresh, so on the
   *      first onLayoutChange those entries are stripped.
   *
   *   2. When children later GROW (the conditional widgets finally render)
   *      AND the props.layouts reference hasn't changed, RGL falls into the
   *      `children changed` branch in its getDerivedStateFromProps and uses
   *      its already-stripped state.layout as the base. New children get
   *      a default `1×1 at (0, bottom(layout))` fallback — the row of tiny
   *      squares the user sees and the source of the negative-width
   *      `<rect>` errors from the squashed charts.
   *
   * Fix:
   *   - Preserve entries for items not in RGL's emitted layout (case 1).
   *   - For each entry RGL emits at exactly w=1,h=1, if we have a saved
   *     larger entry for that id, prefer the saved one (case 2). This
   *     also restores the chart on the very next render because state
   *     updates → props.layouts changes → ResponsiveReactGridLayout
   *     regenerates → inner ReactGridLayout uses the restored layout.
   *
   * We deliberately don't pass `data-grid` on children (which would also
   * fix this) because that would also override RGL's per-breakpoint
   * layout when the breakpoint changes, breaking responsiveness.
   */
  const handleLayoutChange = (_currentLayout: any, allLayouts: any) => {
    setLayouts((prev: any) => {
      const merged: any = {};
      for (const bp of Object.keys(cols) as Array<keyof typeof cols>) {
        const newArr: any[] = Array.isArray(allLayouts?.[bp])
          ? allLayouts[bp]
          : [];
        const prevArr: any[] = Array.isArray(prev?.[bp]) ? prev[bp] : [];
        const prevById = new Map(prevArr.map((l: any) => [l.i, l]));
        // Replace any 1×1 entries (RGL's default fallback for children that
        // appeared without a matching layout entry in its internal state)
        // with the previously-saved entry for that id, when it's larger.
        const repaired = newArr.map((entry: any) => {
          if (entry.w === 1 && entry.h === 1) {
            const prevEntry = prevById.get(entry.i) as any;
            if (prevEntry && (prevEntry.w > 1 || prevEntry.h > 1)) {
              return { ...prevEntry };
            }
          }
          // While editing we render every widget at EDIT_COMPACT_H, so the
          // heights RGL reports are the collapsed ones. Restore each item's
          // real height (from the previous real layout, or its defaults) while
          // keeping the new x/y/w produced by the drag/reorder. This keeps the
          // persisted layout at true sizes even though the edit view is compact.
          if (isEditing && effectiveHeaderOnly) {
            const prevEntry = prevById.get(entry.i) as any;
            const def =
              items.find((it) => it.id === entry.i)?.[bp] ??
              items.find((it) => it.id === entry.i)?.lg;
            return {
              ...entry,
              h: prevEntry?.h ?? def?.h ?? entry.h,
              minH: prevEntry?.minH ?? def?.minH,
              maxH: prevEntry?.maxH ?? def?.maxH,
            };
          }
          return entry;
        });
        const newIds = new Set(repaired.map((l: any) => l.i));
        const preserved = prevArr.filter((l: any) => !newIds.has(l.i));
        merged[bp] =
          preserved.length > 0 ? [...repaired, ...preserved] : repaired;
      }
      // Only persist when not in edit mode; during edit, persist on explicit Save
      if (!isEditing) {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(merged));
        } catch {}
      }
      return merged;
    });
  };

  // Listen for external edit controls from the dashboard menu
  useEffect(() => {
    const handleEditingChange = (e: Event) => {
      const ce = e as CustomEvent<boolean>;
      const editing = Boolean(ce.detail);
      if (editing) {
        // snapshot current state so we can cancel
        preEditLayoutsRef.current = JSON.parse(JSON.stringify(layouts));
        preEditHiddenRef.current = [...hiddenIds];
      }
      setIsEditing(editing);
    };
    const handleSave = () => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(layouts));
      } catch {}
      persistHidden(hiddenIds);
      setIsEditing(false);
    };
    const handleCancel = () => {
      if (preEditLayoutsRef.current) {
        setLayouts(preEditLayoutsRef.current);
      }
      if (preEditHiddenRef.current) {
        setHiddenIds(preEditHiddenRef.current);
      }
      setIsEditing(false);
    };
    window.addEventListener(
      "dashboard:editing-change",
      handleEditingChange as EventListener
    );
    window.addEventListener("dashboard:save-layout", handleSave);
    window.addEventListener("dashboard:cancel-edit", handleCancel);
    return () => {
      window.removeEventListener(
        "dashboard:editing-change",
        handleEditingChange as EventListener
      );
      window.removeEventListener("dashboard:save-layout", handleSave);
      window.removeEventListener("dashboard:cancel-edit", handleCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layouts, hiddenIds, storageKey, hiddenStorageKey]);

  // Support external "restore defaults" action via a window event
  useEffect(() => {
    const resetHandler = () => {
      const defaultHiddenIds = items
        .filter((i) => i.defaultHidden)
        .map((i) => i.id);
      setLayouts(defaultLayouts);
      setHiddenIds(defaultHiddenIds);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(defaultLayouts));
        window.localStorage.setItem(
          hiddenStorageKey,
          JSON.stringify(defaultHiddenIds)
        );
      } catch {}
    };
    window.addEventListener("dashboard:reset-layout", resetHandler);
    return () =>
      window.removeEventListener("dashboard:reset-layout", resetHandler);
  }, [defaultLayouts, storageKey, hiddenStorageKey, items]);

  // While editing, present a collapsed copy of the layout where every widget
  // has the same small height, turning the grid into a compact list of header
  // bars that's fast to reorder. The real `layouts` state keeps true sizes and
  // is what gets saved.
  const displayLayouts = useMemo(() => {
    if (!isEditing || !effectiveHeaderOnly) return layouts;
    const out: any = {};
    for (const bp of Object.keys(cols) as Array<keyof typeof cols>) {
      const arr: any[] = Array.isArray(layouts?.[bp]) ? layouts[bp] : [];
      out[bp] = arr.map((l: any) => ({
        ...l,
        h: EDIT_COMPACT_H,
        minH: EDIT_COMPACT_H,
        maxH: EDIT_COMPACT_H,
      }));
    }
    return out;
  }, [isEditing, effectiveHeaderOnly, layouts]);

  if (!mounted) {
    // Avoid SSR/width issues; render nothing until client mounted
    return null;
  }

  return (
    <div className="relative" dir="ltr">
      {isEditing && hiddenItems.length > 0 && (
        <div
          className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-secondary/30 bg-secondary/5 px-3 py-2"
          dir={lang === "HEB" ? "rtl" : "ltr"}
        >
          <span className="font-Rubik text-xs font-semibold text-menu/80">
            {tr.hiddenCharts}:
          </span>
          {hiddenItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => addChart(item.id)}
              className="transition-colors hover:bg-secondary/15 flex items-center gap-1 rounded-full border border-secondary/40 bg-surface px-2.5 py-1 font-Rubik text-xs text-menu"
              title={tr.addChart}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              <span className="max-w-[180px] truncate">
                {item.title ?? item.id}
              </span>
            </button>
          ))}
          {hiddenItems.length > 1 && (
            <button
              type="button"
              onClick={addAllHiddenCharts}
              className="ms-auto rounded-md border border-secondary/40 bg-secondary/10 px-2.5 py-1 font-Rubik text-xs text-menu hover:bg-secondary/20"
            >
              {tr.addAllHiddenCharts}
            </button>
          )}
        </div>
      )}

      <ResponsiveGridLayout
        className="layout"
        direction="rtl"
        layouts={displayLayouts}
        breakpoints={breakpoints}
        cols={cols}
        rowHeight={28}
        margin={[8, 8]}
        containerPadding={[0, 0]}
        onLayoutChange={handleLayoutChange}
        // Drag only from our top bar to avoid interfering with charts
        draggableHandle=".rgl-drag-handle"
        // Make dragging possible anywhere except interactive/chart areas
        draggableCancel=".react-resizable-handle, button, a, input, textarea, select, .no-drag, svg, path, text, tspan, .recharts-wrapper, .nivo, .nivo__container"
        isDraggable={isEditing}
        isResizable={isEditing}
        isDroppable={false}
        isBounded={false}
        // In the compact (header-only) edit view heights are locked, so only
        // allow horizontal (width) resizing; otherwise expose the full set.
        resizeHandles={
          isEditing && effectiveHeaderOnly
            ? ["e", "w"]
            : ["se", "e", "s", "w", "sw"]
        }
        measureBeforeMount={false}
        useCSSTransforms
        compactType="vertical"
        preventCollision={false}
      >
        {visibleItems.map(({ id, element, title }) => {
          // Header-only edit: render just a compact, draggable header bar so
          // the user can quickly reorder charts without their content.
          if (isEditing && effectiveHeaderOnly) {
            return (
              <div key={id} className="relative h-full">
                <div
                  className="rgl-drag-handle transition-colors flex h-full cursor-move select-none items-center justify-between gap-2 overflow-hidden rounded-md bg-secondary px-3 text-white shadow-md hover:bg-secondary/90"
                  title={dragLabel}
                  dir={lang === "HEB" ? "rtl" : "ltr"}
                >
                  <ArrowsPointingOutIcon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate text-center text-sm font-semibold tracking-wide">
                    {title ?? id}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChart(id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="no-drag flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/70 text-menu hover:bg-red-500 hover:text-white"
                    title={tr.removeChart}
                    aria-label={tr.removeChart}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div
              key={id}
              className={`relative h-full min-h-0 overflow-visible ${
                isEditing ? "rounded-md ring-2 ring-secondary/40" : ""
              }`}
            >
              {isEditing && (
                <div
                  className="rgl-drag-handle transition-colors absolute left-0 right-0 top-0 z-50 flex h-7 cursor-move select-none items-center justify-between gap-2 rounded-t-md bg-secondary px-3 text-white shadow-md hover:bg-secondary/90"
                  title={dragLabel}
                >
                  <ArrowsPointingOutIcon className="h-4 w-4 shrink-0" />
                  <span className="flex flex-1 items-center justify-center gap-1.5 text-[11px] font-semibold tracking-wide">
                    <span aria-hidden className="text-base leading-none">
                      ↑
                    </span>
                    <span aria-hidden className="text-base leading-none">
                      ←
                    </span>
                    <span>{dragLabel}</span>
                    <span aria-hidden className="text-base leading-none">
                      →
                    </span>
                    <span aria-hidden className="text-base leading-none">
                      ↓
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChart(id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="no-drag flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/70 text-menu hover:bg-red-500 hover:text-white"
                    title={tr.removeChart}
                    aria-label={tr.removeChart}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <div className="h-full">{element}</div>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
}
